<template>
  <div class="flex flex-col md:flex-row md:space-x-12 mb-4 md:mb-12">
    <!-- Innovadis custom double title and product codes to fix mobile order -->
    <div v-if="isMobile" class="mb-4">
      <VcTypography tag="h1">{{ product.name }}</VcTypography>

      <div class="flex mt-2 flex-wrap">
        <div
          class="mr-2 bg-neutral-100 px-2 py-1 rounded-md cursor-pointer flex items-center"
          @click="copyText(product.code, $t('pages.product.sku_copied_message'))"
        >
          <span class="text-sm flex items-center">
            {{ $t("pages.product.sku_label") }}

            <span class="mx-1">#{{ product.code }}</span>
            <VcIcon class="mx-1" size="xs" name="document-duplicate" />
          </span>
        </div>

        <div
          class="mr-2 bg-neutral-100 px-2 py-1 rounded-md cursor-pointer flex items-center"
          v-if="product.gtin"
          @click="copyText(product.gtin, $t('pages.product.gtin_copied_message'))"
        >
          <span class="text-sm flex items-center">
            {{ $t("pages.product.gtin_label") }}
            <span class="mx-1">#{{ product.gtin }}</span>
            <VcIcon class="mx-1" size="xs" name="document-duplicate" />
          </span>
        </div>

        <div class="mt-2">
          <InnoInStockProductDetail
            :is-in-stock="product.availabilityData?.isInStock"
            :stock-type="product.sapStockStatus"
            :is-digital="isDigital"
            :quantity="product.availabilityData?.availableQuantity"
          />
        </div>
      </div>
    </div>

    <div ref="imageContainerRef" class="relative mb-4 md:mb-8 md:w-2/5 lg:mb-0">
      <ImageGallery :images="product.images" hide-thumbs class="lg:w-[350px] h-[425px] w-full">
        <template #badges>
          <InnoDiscountBadge :properties="product.properties!" class="absolute left-4 top-4 z-[2]" />
        </template>
      </ImageGallery>

      <div class="absolute right-4 top-4 z-[2]">
        <AddToList :product="product" custom-class="w-6 h-6 lg:w-8 lg:h-8" />
      </div>
    </div>

    <div class="flex flex-col w-full">
      <!-- Innovadis custom double title and product code for desktop -->
      <div v-if="!isMobile">
        <VcTypography tag="h1">{{ product.name }}</VcTypography>

        <div class="flex mt-2">
          <div
            class="mr-2 bg-neutral-100 px-2 py-1 rounded-md cursor-pointer flex items-center"
            @click="copyText(product.code, $t('pages.product.sku_copied_message'))"
          >
            <span class="text-sm flex items-center">
              {{ $t("pages.product.sku_label") }}

              <span class="mx-1">#{{ product.code }}</span>
              <VcIcon class="mx-1" size="xs" name="document-duplicate" />
            </span>
          </div>

          <div
            class="mr-2 bg-neutral-100 px-2 py-1 rounded-md cursor-pointer flex items-center"
            v-if="product.gtin"
            @click="copyText(product.gtin, $t('pages.product.gtin_copied_message'))"
          >
            <span class="text-sm flex items-center">
              {{ $t("pages.product.gtin_label") }}
              <span class="mx-1">#{{ product.gtin }}</span>
              <VcIcon class="mx-1" size="xs" name="document-duplicate" />
            </span>
          </div>

          <InnoInStockProductDetail
            :is-in-stock="product.availabilityData?.isInStock"
            :stock-type="product.sapStockStatus"
            :is-digital="isDigital"
            :quantity="product.availabilityData?.availableQuantity"
          />
        </div>
      </div>

      <p class="mt-2">{{ shortDescription }}</p>
      <div class="mt-6 flex flex-col">
        <VcItemPrice
          v-if="product.sapStockStatus != 3 && product.availabilityData.isBuyable && isAuthenticated"
          :properties="product.properties"
          :value="product.price"
          class="text-3xl !flex-row space-x-2 items-center"
        />

        <InnoDiscountContent class="!text-base mt-4" :properties="product.properties!" />

        <InnoAddToCartProductDetail
          class="mt-7 md:mt-5"
          v-if="isAuthenticated"
          :product="product"
          @added-to-cart="onAddedToCart"
        />

        <div class="w-fit">
          <CountInCart class="mt-2 px-2" :product-id="product.id" />
        </div>

        <template v-if="!isAuthenticated">
          <VcButton :to="{ name: 'SignIn' }" size="md" class="mt-6 w-60">
            {{ $t("custom.product_details.login") }}
          </VcButton>
        </template>

        <div class="flex mt-4 align-center">
          <template v-if="psInFoodProduct && psInFoodProduct.products?.length">
            <div @click="openPsInFoodModal()" class="mr-4 text-sm mt-0.5 cursor-pointer hover:text-secondary-500">
              <VcIcon name="apple-ps-in-food" class="mr-1" :size="20" />

              {{ $t("custom.ps_in_food.button_modal") }}
            </div>
          </template>

          <InnoAddToCompare v-if="$cfg.product_compare_enabled" :product="product" />
        </div>
      </div>
    </div>
  </div>

  <!-- Innovadis custom other components are in inno-product for mobile order -->
  <template v-if="!isMobile">
    <div class="w-full mb-8 md:mt-12">
      <component
        :is="specificationSection?.type"
        :key="specificationSection?.id || 'productspecsA32'"
        :model="specificationSection"
        :product="product"
      />
    </div>

    <div class="flex flex-col">
      <VcTypography tag="h2" class="mb-4 text-3xl">{{
        $t("shared.catalog.product_details.description_block_title")
      }}</VcTypography>

      <VcMarkdownRender :src="description" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { computed, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import productTemplateDefault from "@/config/product-default.json";
import { ProductType } from "@/core/enums";
import { getDynamicPropertyValue } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { InnoAddToCartProductDetail } from "@/shared/cart";
import { useFlyToCart } from "@/shared/cart";
import {
  ImageGallery,
  InnoDiscountContent,
  InnoDiscountBadge,
  CountInCart,
  InnoInStockProductDetail,
} from "@/shared/catalog";
import { InnoAddToCompare } from "@/shared/compare";
import { useModal } from "@/shared/modal";
import { AddToList } from "@/shared/wishlists";
import { InnoPsInFoodModal } from "@/ui-kit/components";
import type { Product, ProductSearchResultType } from "@/core/api/graphql/types";
import type { IPageTemplate } from "@/shared/static-content";
import type { PropType } from "vue";
import { useNotifications } from "@/shared/notification";
import { useI18n } from "vue-i18n";

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },

  psInFoodProduct: {
    type: Object as PropType<ProductSearchResultType>,
    required: false,
  },

  loadingPsInFood: {
    type: Boolean,
    required: false,
  },
});

const firstImageUrl = computed(() => props.product?.images?.[0]?.url);

useHead({
  link: () =>
    firstImageUrl.value ? [{ rel: "preload", href: firstImageUrl.value, as: "image", fetchpriority: "high" }] : [],
});

const { isAuthenticated } = useUser();

const imageContainerRef = ref<HTMLElement | null>(null);
const { flyToCart } = useFlyToCart();

function onAddedToCart() {
  flyToCart(imageContainerRef.value);
}

const productTemplate = computed(() => {
  return productTemplateDefault as IPageTemplate;
});
const { t } = useI18n();

const breakpoints = useBreakpoints(breakpointsTailwind);

const isMobile = breakpoints.smaller("lg");

const PROMOTION_ACTIVE_PROPERTY = "promotionactive_property";

const promotionActive = computed<boolean>(
  () => getDynamicPropertyValue(props.product.properties, PROMOTION_ACTIVE_PROPERTY) ?? false,
);

const isDigital = computed<boolean>(() => props.product.productType === ProductType.Digital);

const SHORT_DESCRIPTION_PROPERTY = "korteomschrijving_property"; // was KorteOmschrijving but was changed as result of solution for duplicate properties
const DESCRIPTION_PROPERTY = "omschrijving_property";
const productInfo = productTemplate?.value.content?.find((item) => item?.type === "product-info");

const specificationSection = productInfo?.blocks?.find((item) => item?.type === "product-properties");
const { copy } = useClipboard();
const notifications = useNotifications();

const shortDescription = computed<string>(
  () => getDynamicPropertyValue(props.product?.properties, SHORT_DESCRIPTION_PROPERTY) ?? "",
);

const description = computed<string>(
  () => getDynamicPropertyValue(props.product?.properties, DESCRIPTION_PROPERTY) ?? "",
);

const { openModal } = useModal();

const copyText = async (text: string, message: string) => {
  await copy(text);

  notifications.success({
    text: message,
    duration: 4000,
    single: true,
  });
};

function openPsInFoodModal() {
  openModal({
    component: InnoPsInFoodModal,
    props: {
      psProduct: props.psInFoodProduct,
    },
  });
}
</script>

<style>
@media (min-width: 1200px) {
  .custom-titlte-prod-detail {
    font-size: 3rem;
    line-height: 52px;
  }
}
</style>
